import { useState, useEffect, useCallback } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { usePermissions } from '../../hooks/usePermissions';
// import KDPInvoices from '../../kdp-invoices/KDPInvoices';

// --- SVG Icon Components for a cleaner UI ---
const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
);

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
    </svg>
);

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const PermissionManagement = () => {
    const permissionAccess = usePermissions(['Permission Management', 'hr/Employee Management/Permission Management']);
    const [permissions, setPermissions] = useState([]);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingPermission, setEditingPermission] = useState(null);
    const [formData, setFormData] = useState({
        name: '', module: '', action: [], description: ''
    });

    const { loading, error, makeRequest, clearError } = useAuthenticatedApi();

    const modules = [
        'Incoming Applications', 'Accepted Applications', 'Rejected Applications', 'Holded Applications',
        'KDP Users', 'KD Users', 'Order Details', 'Categories', 'Shop Fees', 'Subscription Plans', 'Size Charts',
        'Wallet', 'HSN Codes', 'Product Policies', 'Announcements', 'Notifications',
        'Support Tickets', 'Privacy Policy', 'Terms of Use', 'Refund & Return', 'Contact Forms',
        'Tutorials', 'Employee Management', 'Role Management', 'Permission Management', 'Attendance', 'My Attendance',
        'hr/Employee Management/Employee Management', 'hr/Employee Management/Role Management', 'hr/Employee Management/Permission Management', 'hr/Employee Management/Attendance', 'hr/Employee Management/My Attendance',
        'COO Dashboard', 'CTO Dashboard', 'Shop Management', 'Service Categories', 'Service Bookings',
        'Customer Suggestions', 'Shop Offers', 'Shop Products', 'Shop Requests', 'KDP Invoices',
        'Visitor Analytics',
        'Operations', 'orders',
        'executive/marketing', 'executive/legal',
        'finance/orderinvoices', 'finance/kdpinvoices', 'finance/wallet transactions',
        // Operations department (role/department-based)
        'operations/Shop Management', 'operations/Shop Fees', 'operations/Subscription Plans', 'operations/HSN Codes', 'operations/GST Type',
        'operations/bpo/orders', 'operations/bpo/service-bookings',
        'operations/bpo/onboarding/Incoming Applications', 'operations/bpo/onboarding/Accepted Applications', 'operations/bpo/onboarding/Rejected Applications',
        'operations/bpo/users/KD Users', 'operations/bpo/users/KDP Users',
        'operations/bpo/apps/Size Charts', 'operations/bpo/apps/Tutorials', 'operations/bpo/apps/HSN Codes', 'operations/bpo/apps/Announcements', 'operations/bpo/apps/Categories', 'operations/bpo/apps/Service Categories',
        'operations/bpo/support/Support Tickets', 'operations/bpo/support/Contact Forms', 'operations/bpo/support/Customer Suggestions',
        'operations/bpo/kdpsupport/Support Tickets', 'operations/bpo/kdpsupport/Contact Forms'
    ];

    modules.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })); // Sort modules alphabetically, case-insensitive

    const actions = ['CREATE', 'READ', 'UPDATE', 'DELETE'];

    const fetchPermissions = useCallback(async () => {
        const response = await makeRequest(async () => apiWithAuth.get('/permissions'));
        if (response) {
            setPermissions(response.data || []);
        } else {
            setPermissions([]);
        }
    }, [makeRequest]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const resetFormData = () => {
        setFormData({ name: '', module: '', action: [], description: '' });
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingPermission(null);
        resetFormData();
        clearError();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();
        if (!Array.isArray(formData.action) || formData.action.length === 0) {
            alert('Please select at least one action');
            return;
        }
        const url = editingPermission
            ? `/permissions/${editingPermission._id}`
            : `/permissions`;
        const method = editingPermission ? 'put' : 'post';

        const payload = {
            name: formData.name,
            module: formData.module,
            action: (formData.action || []).map(a => a.toString().toUpperCase()),
            description: formData.description || ''
        };

        const response = await makeRequest(async () => apiWithAuth[method](url, payload));

        if (response) {
            handleCancelForm();
            fetchPermissions();
        }
    };

    const handleEdit = (permission) => {
        setEditingPermission(permission);
        setFormData({
            name: permission.name,
            module: permission.module,
            action: (Array.isArray(permission.action)
                ? permission.action.map(a => a?.toString().toUpperCase())
                : (permission.action ? [permission.action.toString().toUpperCase()] : [])
            ).filter(a => a !== 'MANAGE'),
            description: permission.description || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (permissionId) => {
        if (window.confirm('Are you sure you want to delete this permission? It might be in use by some roles.')) {
            const response = await makeRequest(async () => apiWithAuth.delete(`/permissions/${permissionId}`));
            if (response) {
                fetchPermissions();
            }
        }
    };

    const filteredPermissions = permissions.filter(p =>
        Object.values(p).some(value =>
            String(value).toLowerCase().includes(search.toLowerCase())
        )
    );

    // Group permissions by module
    const groupedPermissions = filteredPermissions.reduce((acc, permission) => {
        const module = permission.module || 'Other';
        if (!acc[module]) {
            acc[module] = [];
        }
        acc[module].push(permission);
        return acc;
    }, {});

    // Sort modules alphabetically
    const sortedModules = Object.keys(groupedPermissions).sort();

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 text-center">Permission Management</h1>
                        <p className="mt-1 text-gray-600 text-center">Control access by creating and assigning specific permissions.</p>
                    </div>
                    <button
                        className="mt-4 sm:mt-0 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        onClick={() => {
                            setEditingPermission(null);
                            resetFormData();
                            setShowForm(true);
                        }}
                    >
                        <PlusIcon />
                        Add Permission
                    </button>
                </header>

                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="relative mb-4">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search permissions..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    {error && !showForm && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert"><p>{error}</p></div>}

                    {loading ? (
                        <div className="text-center py-10">Loading...</div>
                    ) : filteredPermissions.length === 0 ? (
                        <div className="text-center py-10">No permissions found.</div>
                    ) : (
                        <div className="space-y-6">
                            {sortedModules.map(module => (
                                <div key={module} className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-900 capitalize">
                                            {module.replace(/_/g, ' ')}
                                            <span className="ml-2 text-sm font-normal text-gray-500">
                                                ({groupedPermissions[module].length} permission{groupedPermissions[module].length !== 1 ? 's' : ''})
                                            </span>
                                        </h3>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left text-gray-500">
                                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3">Name</th>
                                                    <th scope="col" className="px-6 py-3">Permissions</th>
                                                    <th scope="col" className="px-6 py-3">Description</th>
                                                    <th scope="col" className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupedPermissions[module].map(p => (
                                                    <tr key={p._id} className="bg-white border-b hover:bg-gray-50">
                                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{p.name}</td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-wrap gap-1">
                                                                {Array.isArray(p.action) ?
                                                                    p.action.map((act, index) => (
                                                                        <span key={index} className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                                                            {(act ?? '').toString().toUpperCase()}
                                                                        </span>
                                                                    )) :
                                                                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                                                        {(p.action ?? '').toString().toUpperCase()}
                                                                    </span>
                                                                }
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-gray-600">{p.description || '—'}</td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex justify-end items-center space-x-2">
                                                                <button onClick={() => handleEdit(p)} className="p-2 text-gray-500 hover:text-indigo-600 group relative">
                                                                    <EditIcon />
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Edit</span>
                                                                </button>
                                                                <button onClick={() => handleDelete(p._id)} className="p-2 text-gray-500 hover:text-red-600 group relative">
                                                                    <DeleteIcon />
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Delete</span>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Permission Modal */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showForm ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
                    <header className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">{editingPermission ? 'Edit Permission' : 'Create New Permission'}</h2>
                        <button onClick={handleCancelForm} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><CloseIcon /></button>
                    </header>
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto space-y-6">
                            {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert"><p>{error}</p></div>}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name</label>
                                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
                                    <select value={formData.module} onChange={(e) => setFormData({ ...formData, module: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required>
                                        <option value="">Select Module</option>
                                        {modules.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {actions.map(a => {
                                            const checked = (formData.action || []).includes(a);
                                            return (
                                                <label key={a} className="inline-flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        checked={checked}
                                                        onChange={(e) => {
                                                            setFormData(prev => {
                                                                const current = Array.isArray(prev.action) ? prev.action : [];
                                                                if (e.target.checked) {
                                                                    return { ...prev, action: Array.from(new Set([...current, a])) };
                                                                }
                                                                return { ...prev, action: current.filter(x => x !== a) };
                                                            });
                                                        }}
                                                    />
                                                    <span>{a}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"></textarea>
                            </div>
                        </div>
                        <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t mt-auto">
                            <button type="button" onClick={handleCancelForm} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">{editingPermission ? 'Update Permission' : 'Create Permission'}</button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PermissionManagement;